/**
 * Catalog of Pill peripheral-mode options rendered by the mode selector.
 *
 * Pure data builder for reuse and unit-testing outside Svelte.
 */

import { format } from 'svelte-i18n';
import { get } from 'svelte/store';
import type { Device } from 'src/types/Device';
import type { DeviceSettingsG2 } from 'src/types/DeviceSettings';
import type { SettingRequest } from 'src/types/requests/settings/setting';
import { gen2Request } from 'src/services/gen_2_http_reqs';
import { hasLEDStripSupport, hasUARTSupport } from './thepill_data_helpers';

/**
 * A single Pill peripheral-mode option.
 *
 * `hidden` is an optional predicate from the firmware-reported mode list;
 */
export type PillModeOption = {
	title: string;
	icon: string;
	description: string;
	value: string;
	disabled?: boolean;
	hidden?: (modes: string[]) => boolean;
};

type PillModeEntries = Record<string, PillModeOption>;

/**
 * Build the catalog of Pill peripheral-mode options.
 *
 * Svelte callers should invoke this inside a reactive statement that
 * already references `$format`, so locale changes trigger a re-run and
 * fresh translations are read via `get(format)` below.
 */
export const getPillModeEntries = (): PillModeEntries => {
	const _format = get(format);
	return {
		onewire: {
			title: _format('settings_pill_options_onewire', { default: 'Temperature' }),
			icon: 'fal fa-temperature-high',
			description: _format('settings_pill_options_onewire_description', {
				default: 'Sensor: up to 5x DS18B20',
			}),
			value: 'onewire',
		},
		dht22: {
			title: _format('settings_pill_options_dht22', { default: 'Temp. & Humidity' }),
			icon: 'fal fa-temperature-list',
			description: _format('settings_pill_options_dht22_description', {
				default: 'Sensor: DHT22',
			}),
			value: 'dht22',
		},
		analog_in: {
			title: _format('settings_pill_options_analog_in', {
				default: 'Analog Input (Voltmeter)',
			}),
			icon: 'fal fa-gauge-circle-bolt',
			description: _format('settings_pill_options_analog_in_description', {
				default: 'Voltmeter',
			}),
			value: 'analog_in',
		},
		ssr: {
			title: _format('settings_pill_options_ssr', { default: 'SSR Add-on' }),
			icon: 'fal fa-toggle-on',
			description: _format('settings_pill_options_ssr_description', {
				default: 'Dual-Channel Solid State Relay Add-on',
			}),
			value: 'ssr',
		},
		digital_io: {
			title: _format('settings_pill_options_digital_io', { default: 'Digital Input/Output' }),
			icon: 'fal fa-arrows-alt-h',
			description: _format('settings_pill_options_digital_io_description', {
				default: '3x Digital IO',
			}),
			value: 'digital_io',
		},
		uart: {
			// Legacy `uart` value, kept for backward compatibility.
			// Lifecycle:
			// - FW without UART support: disabled in the default list
			// - FW with UART support but no `attrs.modes`: enabled as "Beta".
			// - FW that advertises `js_uart` via `attrs.modes`: hidden &
			//  superseded by the `js_uart` entry.
			title: _format('settings_pill_options_uart', {
				default: _format('settings_pill_options_uart_description', {
					default: 'Serial Communication (UART)',
				}),
			}),
			icon: 'fal fa-arrow-down-arrow-up',
			description: _format('info_message_beta_feature', {
				default: 'Beta feature - use with caution.',
			}),
			value: 'uart',
			hidden: (modes) => modes.includes('js_uart'),
		},
		js_uart: {
			title: _format('settings_pill_options_uart', {
				default: _format('settings_pill_options_uart_description', {
					default: 'Serial Communication (UART)',
				}),
			}),
			icon: 'fal fa-arrow-down-arrow-up',
			description: _format('settings_pill_options_js_uart_description', {
				default: 'Communication with external sensors and systems',
			}),
			value: 'js_uart',
		},
		mb_client: {
			title: _format('settings_pill_options_mb_client', {
				default: 'Modbus RTU Client (RS485 Add-on)',
			}),
			icon: 'fal fa-network-wired',
			description: _format('settings_pill_options_mb_client_description', {
				default: 'Modbus RTU Client for external RS485 devices',
			}),
			value: 'mb_client',
		},
		ledstrip: {
			title: _format('settings_pill_options_led_strip', { default: 'LED Strip' }),
			icon: 'fal fa-lightbulb',
			description: _format('settings_pill_options_led_strip_description', {
				default: 'Addressable LED Strip Control',
			}),
			value: 'ledstrip',
		},
	};
};

/**
 * Static default mode list used as a fallback when the firmware does not
 * expose `attrs.modes`.
 *
 * `uart` is disabled when `uartSupported` is false (older firmwares that
 * don't yet implement it);
 * @ToDo: `ledstrip` is appended for test users. Remove when dev FW is available.
 */
export const getDefaultPillModes = (
	uartSupported: boolean,
	ledStripSupported: boolean,
): PillModeOption[] => {
	const entries = getPillModeEntries();
	const list: PillModeOption[] = [
		entries.onewire,
		entries.dht22,
		entries.analog_in,
		entries.ssr,
		entries.digital_io,
		{ ...entries.uart, disabled: !uartSupported },
	];
	if (ledStripSupported) list.push(entries.ledstrip);
	return list;
};

/**
 * Fetch the list of supported Pill peripheral modes from the firmware.
 * The list is exposed by `Shelly.GetComponents` as `components[0].attrs.modes`
 * on newer firmwares.
 * NOTE: Older firmwares don't return `attrs.modes`  (before 2.0.0-beta release).
 *
 * @param device The Pill device.
 * @returns The list of supported mode ids, or `null` when unavailable.
 */
export const fetchPillSupportedModes = async (device: Device): Promise<string[] | null> => {
	try {
		const res: SettingRequest = await gen2Request(device, 'Shelly.GetComponents', {
			keys: ['pill'],
		});
		if (!res?.isok) return null;
		const data = res?.data ?? res;
		const pillComponent = data?.components?.[0];
		const modes = pillComponent?.attrs?.modes;
		return Array.isArray(modes) && modes.length ? modes : null;
	} catch {
		return null;
	}
};

/**
 * Resolve the Pill peripheral-mode options for a device.
 *
 * Prefers the firmware-advertised list (via `attrs.modes`) and falls back
 * to the static defaults gated by UART / LED-strip firmware support.
 */
export const getPillModes = async (
	device: Device,
	settings: DeviceSettingsG2,
): Promise<PillModeOption[]> => {
	const modes = await fetchPillSupportedModes(device);
	if (modes) {
		const entries = getPillModeEntries();
		return modes
			.map((m) => entries[m])
			.filter((opt): opt is PillModeOption => opt && !opt.hidden?.(modes));
	}
	return getDefaultPillModes(hasUARTSupport(settings), hasLEDStripSupport(settings));
};
